import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMicrochip,
    faServer,
    faPlay,
    faStop,
    faTrash,
    faPlus,
    faInfoCircle,
    faDollarSign,
    faNetworkWired,
    faTerminal,
    faCopy,
    faRefresh,
    faTimes,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import gpuApi, { GPUInstance, GPUSpec, GPUStats, HealthCheck } from './gpuApi';
import PurchaseWizard, { PurchaseData } from './PurchaseWizard';

const GPUsTab: React.FC = () => {
    const [view, setView] = useState<'instances' | 'marketplace'>('instances');
    const [instances, setInstances] = useState<GPUInstance[]>([]);
    const [catalog, setCatalog] = useState<GPUSpec[]>([]);
    const [stats, setStats] = useState<GPUStats | null>(null);
    const [healthCheck, setHealthCheck] = useState<HealthCheck | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedInstance, setSelectedInstance] = useState<GPUInstance | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // Purchase Wizard
    const [showPurchaseWizard, setShowPurchaseWizard] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<GPUSpec | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            if (view === 'instances') {
                fetchInstances();
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [view]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [instancesData, catalogData, statsData, health] = await Promise.all([
                gpuApi.getInstances(),
                gpuApi.getCatalog(),
                gpuApi.getStats(),
                gpuApi.healthCheck()
            ]);
            setInstances(instancesData);
            setCatalog(catalogData.data);
            setStats(statsData);
            setHealthCheck(health);
        } catch (error) {
            console.error('Error fetching GPU data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchInstances = async () => {
        try {
            const [instancesData, statsData] = await Promise.all([
                gpuApi.getInstances(),
                gpuApi.getStats()
            ]);
            setInstances(instancesData);
            setStats(statsData);
        } catch (error) {
            console.error('Error fetching instances:', error);
        }
    };

    const openPurchaseWizard = (config: GPUSpec) => {
        setSelectedConfig(config);
        setShowPurchaseWizard(true);
    };

    const handlePurchaseSubmit = async (data: PurchaseData) => {
        if (!selectedConfig) return;

        try {
            // Provisioning happens only after payment confirms (see
            // gpuOrderService.js) — redirect to Stripe instead of creating
            // the instance synchronously. The instance appears in the
            // dashboard once the user returns and the purchase is confirmed
            // (picked up by the existing polling interval below).
            const { checkout } = await gpuApi.createCheckout({
                configId: data.configId,
                name: data.name,
                region: data.region,
                sshKeyName: data.sshKeyName || undefined,
                contractTerm: data.contractTerm
            });
            window.location.href = checkout.url;
        } catch (error: any) {
            alert(`Failed to start checkout: ${error.response?.data?.error || error.message}`);
            throw error;
        }
    };

    const handleInstanceAction = async (action: 'start' | 'stop' | 'terminate', instance: GPUInstance) => {
        if (action === 'terminate') {
            const confirm = window.confirm(`Are you sure you want to terminate "${instance.name}"? This action cannot be undone.`);
            if (!confirm) return;
        }

        setActionLoading(instance.id);
        try {
            switch (action) {
                case 'start':
                    await gpuApi.startInstance(instance.id);
                    break;
                case 'stop':
                    await gpuApi.stopInstance(instance.id);
                    break;
                case 'terminate':
                    await gpuApi.terminateInstance(instance.id, 'User requested termination');
                    break;
            }
            await fetchInstances();
        } catch (error: any) {
            alert(`Action failed: ${error.response?.data?.message || error.message}`);
        } finally {
            setActionLoading(null);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'running': return 'text-green-700 bg-green-50';
            case 'stopped': return 'text-gray-700 bg-gray-100';
            case 'provisioning': case 'starting': return 'text-blue-700 bg-blue-50';
            case 'stopping': return 'text-yellow-700 bg-yellow-50';
            case 'terminated': case 'error': return 'text-red-700 bg-red-50';
            default: return 'text-gray-700 bg-gray-100';
        }
    };

    if (loading) {
        return (
            <div className="p-4 flex items-center justify-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-blue-600" />
                <span className="ml-3 text-gray-600 font-mono text-sm">Loading GPU data...</span>
            </div>
        );
    }

    return (
        <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
            {/* Header */}
            <div className="bg-white border border-gray-300 rounded p-3">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faMicrochip} className="text-blue-600" />
                            <h1 className="text-base font-bold text-gray-900">GPU Servers</h1>
                            <span className="text-xs px-2 py-0.5 rounded font-medium bg-blue-100 text-blue-700">
                                DevOpsX Marketplace
                            </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                            High-performance GPU instances for AI/ML workloads • {catalog.length} configurations available
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => fetchData()}
                            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium"
                        >
                            <FontAwesomeIcon icon={faRefresh} className="mr-1" />
                            Refresh
                        </button>
                        <button
                            onClick={() => setView(view === 'instances' ? 'marketplace' : 'instances')}
                            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                        >
                            <FontAwesomeIcon icon={view === 'instances' ? faPlus : faServer} className="mr-1" />
                            {view === 'instances' ? 'Browse GPUs' : 'My Instances'}
                        </button>
                    </div>
                </div>
            </div>

            {view === 'instances' ? (
                <>
                    {/* Stats */}
                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <div className="bg-white border border-gray-300 rounded p-2">
                                <div className="text-xs text-gray-600 font-medium">Total Instances</div>
                                <div className="text-xl font-bold">{stats.total}</div>
                            </div>
                            <div className="bg-white border border-green-300 rounded p-2">
                                <div className="text-xs text-gray-600 font-medium">Running</div>
                                <div className="text-xl font-bold text-green-700">{stats.running}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-2">
                                <div className="text-xs text-gray-600 font-medium">Stopped</div>
                                <div className="text-xl font-bold text-gray-700">{stats.stopped}</div>
                            </div>
                            <div className="bg-white border border-blue-300 rounded p-2">
                                <div className="text-xs text-gray-600 font-medium">Current Cost</div>
                                <div className="text-xl font-bold text-blue-700">${stats.totalCurrentCost.toFixed(2)}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-2">
                                <div className="text-xs text-gray-600 font-medium">Est. Monthly</div>
                                <div className="text-xl font-bold">${stats.totalMonthlyCost.toFixed(2)}</div>
                            </div>
                        </div>
                    )}

                    {/* Instances Table */}
                    <div className="bg-white border border-gray-300 rounded overflow-hidden">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-100 border-b border-gray-300">
                                <tr>
                                    <th className="text-left p-2 font-bold">Name</th>
                                    <th className="text-left p-2 font-bold">GPU</th>
                                    <th className="text-left p-2 font-bold">Status</th>
                                    <th className="text-left p-2 font-bold">Region</th>
                                    <th className="text-left p-2 font-bold">IP Address</th>
                                    <th className="text-left p-2 font-bold">Uptime</th>
                                    <th className="text-left p-2 font-bold">Cost</th>
                                    <th className="text-right p-2 font-bold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {instances.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center p-8 text-gray-500">
                                            <FontAwesomeIcon icon={faMicrochip} className="text-4xl mb-2 block mx-auto text-gray-300" />
                                            <div className="font-medium">No GPU instances found</div>
                                            <div className="text-xs mt-1">Click "Browse GPUs" to launch your first instance</div>
                                        </td>
                                    </tr>
                                ) : (
                                    instances.map((instance) => (
                                        <tr key={instance.id} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="p-2">
                                                <div className="font-bold">{instance.name}</div>
                                                <div className="text-xs text-gray-500">ID: {instance.id.slice(-8)}</div>
                                            </td>
                                            <td className="p-2">
                                                <div className="font-medium">{instance.gpuCount}x {instance.gpuType}</div>
                                                <div className="text-xs text-gray-500">{instance.specifications.vram}</div>
                                            </td>
                                            <td className="p-2">
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(instance.status)}`}>
                                                    {instance.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="p-2 font-medium">{instance.region}</td>
                                            <td className="p-2">
                                                {instance.network?.publicIP ? (
                                                    <div className="flex items-center gap-1">
                                                        <code className="text-xs font-bold">{instance.network.publicIP}</code>
                                                        <button
                                                            onClick={() => copyToClipboard(instance.network.publicIP!)}
                                                            className="text-blue-600 hover:text-blue-800"
                                                        >
                                                            <FontAwesomeIcon icon={faCopy} className="text-xs" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">Provisioning...</span>
                                                )}
                                            </td>
                                            <td className="p-2 font-medium">{instance.uptimeString || '0 hours'}</td>
                                            <td className="p-2">
                                                <div className="font-bold">${instance.currentCost?.toFixed(2) || '0.00'}</div>
                                                <div className="text-xs text-gray-500">${instance.billing?.pricePerMonth || '0'}/mo</div>
                                            </td>
                                            <td className="p-2">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => setSelectedInstance(instance)}
                                                        className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                                                        title="View Details"
                                                    >
                                                        <FontAwesomeIcon icon={faInfoCircle} />
                                                    </button>
                                                    {instance.status === 'stopped' && (
                                                        <button
                                                            onClick={() => handleInstanceAction('start', instance)}
                                                            disabled={actionLoading === instance.id}
                                                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                                            title="Start"
                                                        >
                                                            <FontAwesomeIcon icon={actionLoading === instance.id ? faSpinner : faPlay} spin={actionLoading === instance.id} />
                                                        </button>
                                                    )}
                                                    {instance.status === 'running' && (
                                                        <button
                                                            onClick={() => handleInstanceAction('stop', instance)}
                                                            disabled={actionLoading === instance.id}
                                                            className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
                                                            title="Stop"
                                                        >
                                                            <FontAwesomeIcon icon={actionLoading === instance.id ? faSpinner : faStop} spin={actionLoading === instance.id} />
                                                        </button>
                                                    )}
                                                    {instance.status !== 'terminated' && (
                                                        <button
                                                            onClick={() => handleInstanceAction('terminate', instance)}
                                                            disabled={actionLoading === instance.id}
                                                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                                            title="Terminate"
                                                        >
                                                            <FontAwesomeIcon icon={actionLoading === instance.id ? faSpinner : faTrash} spin={actionLoading === instance.id} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                /* Marketplace - GPU Catalog */
                <div className="space-y-3">
                    <div className="bg-white border border-gray-300 rounded p-3">
                        <h2 className="text-sm font-bold mb-1">GPU Marketplace</h2>
                        <p className="text-xs text-gray-600">Select a GPU configuration to launch an instance • On-demand pricing • Instant provisioning</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {catalog.map((config) => (
                            <div key={config.id} className="bg-white border border-gray-300 rounded p-3 hover:border-blue-400 transition-colors">
                                <div className="mb-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className="text-sm font-bold">{config.name}</h3>
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">
                                            {config.gpuCount}x {config.gpuType}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-600 font-medium">{config.specifications.vram}</p>
                                </div>

                                <div className="space-y-1 text-xs mb-3 border-t border-gray-200 pt-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">CPU:</span>
                                        <span className="font-bold text-right">{config.specifications.cpu}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Cores:</span>
                                        <span className="font-bold">{config.specifications.cpuCores}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">RAM:</span>
                                        <span className="font-bold">{config.specifications.ram}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Storage:</span>
                                        <span className="font-bold">{config.specifications.storage}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">CUDA Cores:</span>
                                        <span className="font-bold">{config.specifications.cudaCores.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Tensor Cores:</span>
                                        <span className="font-bold">{config.specifications.tensorCores}</span>
                                    </div>
                                </div>

                                <div className="bg-green-50 border border-green-200 rounded p-2 mb-2">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="text-xs text-gray-600 font-medium">Monthly</div>
                                            <div className="font-bold text-base text-green-700">${config.pricing.monthly}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-gray-600 font-medium">Hourly</div>
                                            <div className="font-bold text-sm">${config.pricing.hourly}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="text-xs text-gray-600 mb-2 font-medium">
                                    {config.regions.length} region{config.regions.length !== 1 ? 's' : ''} available
                                </div>

                                <button
                                    onClick={() => openPurchaseWizard(config)}
                                    className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-bold"
                                >
                                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                    Launch Instance
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Purchase Wizard */}
            {showPurchaseWizard && selectedConfig && (
                <PurchaseWizard
                    config={selectedConfig}
                    onClose={() => {
                        setShowPurchaseWizard(false);
                        setSelectedConfig(null);
                    }}
                    onSubmit={handlePurchaseSubmit}
                />
            )}

            {/* Instance Details Modal */}
            {selectedInstance && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-blue-600 px-4 py-3 flex items-center justify-between border-b rounded-t">
                            <div className="text-white">
                                <h3 className="font-bold text-base">{selectedInstance.name}</h3>
                                <p className="text-xs text-blue-100">Instance ID: {selectedInstance.id}</p>
                            </div>
                            <button
                                onClick={() => setSelectedInstance(null)}
                                className="text-white hover:text-blue-200"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="p-4 space-y-3">
                            {/* Instance Info */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faServer} />
                                    Instance Information
                                </h4>
                                <table className="w-full text-xs">
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="py-1 pr-4 text-gray-600 font-medium w-1/3">Status</td>
                                            <td className="py-1">
                                                <span className={`px-2 py-0.5 rounded font-bold ${getStatusColor(selectedInstance.status)}`}>
                                                    {selectedInstance.status.toUpperCase()}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="py-1 pr-4 text-gray-600 font-medium">GPU</td>
                                            <td className="py-1 font-bold">{selectedInstance.gpuCount}x NVIDIA {selectedInstance.gpuType}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="py-1 pr-4 text-gray-600 font-medium">Region</td>
                                            <td className="py-1 font-bold">{selectedInstance.region}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="py-1 pr-4 text-gray-600 font-medium">Created</td>
                                            <td className="py-1 font-medium">{new Date(selectedInstance.createdAt).toLocaleString()}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4 text-gray-600 font-medium">Uptime</td>
                                            <td className="py-1 font-bold">{selectedInstance.uptimeString || '0 hours'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Hardware Specs */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-2">Hardware Specifications</h4>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    <div className="flex justify-between border-b py-1">
                                        <span className="text-gray-600 font-medium">CPU:</span>
                                        <span className="font-bold">{selectedInstance.specifications.cpu}</span>
                                    </div>
                                    <div className="flex justify-between border-b py-1">
                                        <span className="text-gray-600 font-medium">Cores:</span>
                                        <span className="font-bold">{selectedInstance.specifications.cpuCores}</span>
                                    </div>
                                    <div className="flex justify-between border-b py-1">
                                        <span className="text-gray-600 font-medium">RAM:</span>
                                        <span className="font-bold">{selectedInstance.specifications.ram}</span>
                                    </div>
                                    <div className="flex justify-between border-b py-1">
                                        <span className="text-gray-600 font-medium">Storage:</span>
                                        <span className="font-bold">{selectedInstance.specifications.storage}</span>
                                    </div>
                                    <div className="flex justify-between border-b py-1">
                                        <span className="text-gray-600 font-medium">VRAM:</span>
                                        <span className="font-bold">{selectedInstance.specifications.vram}</span>
                                    </div>
                                    <div className="flex justify-between border-b py-1">
                                        <span className="text-gray-600 font-medium">Bandwidth:</span>
                                        <span className="font-bold">{selectedInstance.specifications.bandwidth}</span>
                                    </div>
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">CUDA Cores:</span>
                                        <span className="font-bold">{selectedInstance.specifications.cudaCores.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">Tensor Cores:</span>
                                        <span className="font-bold">{selectedInstance.specifications.tensorCores}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Network */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faNetworkWired} />
                                    Network
                                </h4>
                                <div className="space-y-1 text-xs">
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">Public IP:</span>
                                        {selectedInstance.network?.publicIP ? (
                                            <div className="flex items-center gap-2">
                                                <code className="font-bold">{selectedInstance.network.publicIP}</code>
                                                <button
                                                    onClick={() => copyToClipboard(selectedInstance.network.publicIP!)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    <FontAwesomeIcon icon={faCopy} />
                                                </button>
                                            </div>
                                        ) : <span className="text-gray-400">Provisioning...</span>}
                                    </div>
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">Private IP:</span>
                                        {selectedInstance.network?.privateIP ? (
                                            <div className="flex items-center gap-2">
                                                <code className="font-bold">{selectedInstance.network.privateIP}</code>
                                                <button
                                                    onClick={() => copyToClipboard(selectedInstance.network.privateIP!)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    <FontAwesomeIcon icon={faCopy} />
                                                </button>
                                            </div>
                                        ) : <span className="text-gray-400">N/A</span>}
                                    </div>
                                </div>
                            </div>

                            {/* SSH Access */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faTerminal} />
                                    SSH Access
                                </h4>
                                <div className="text-xs mb-2">
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">SSH Key:</span>
                                        <span className="font-bold">{selectedInstance.ssh.keyName}</span>
                                    </div>
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">Username:</span>
                                        <span className="font-bold">{selectedInstance.ssh.username}</span>
                                    </div>
                                </div>
                                {selectedInstance.network?.publicIP && (
                                    <div className="bg-gray-900 text-green-400 p-2 rounded font-mono text-xs overflow-x-auto">
                                        ssh -i ~/.ssh/{selectedInstance.ssh.keyName}.pem {selectedInstance.ssh.username}@{selectedInstance.network.publicIP}
                                    </div>
                                )}
                            </div>

                            {/* Billing */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faDollarSign} />
                                    Billing Information
                                </h4>
                                <div className="text-xs space-y-1">
                                    <div className="flex justify-between py-1 border-b">
                                        <span className="text-gray-600 font-medium">Current Month Cost:</span>
                                        <span className="font-bold text-blue-700">${selectedInstance.billing?.currentMonthCost?.toFixed(2) || '0.00'}</span>
                                    </div>
                                    <div className="flex justify-between py-1 border-b">
                                        <span className="text-gray-600 font-medium">Hourly Rate:</span>
                                        <span className="font-bold">${selectedInstance.billing?.pricePerHour?.toFixed(2) || '0.00'}/hour</span>
                                    </div>
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-600 font-medium">Est. Monthly:</span>
                                        <span className="font-bold">${selectedInstance.billing?.pricePerMonth?.toFixed(2) || '0.00'}/month</span>
                                    </div>
                                </div>
                            </div>

                            {/* Software */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-2">Pre-installed Software</h4>
                                <div className="text-xs">
                                    <div className="mb-2"><strong>OS:</strong> {selectedInstance.metadata.operatingSystem}</div>
                                    <div className="flex flex-wrap gap-1">
                                        {selectedInstance.metadata.installedSoftware.map((software) => (
                                            <span key={software} className="bg-gray-100 px-2 py-0.5 rounded text-xs font-medium">
                                                {software}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-4 py-3 border-t flex justify-end rounded-b">
                            <button
                                onClick={() => setSelectedInstance(null)}
                                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GPUsTab;
